import type { ReactElement } from 'react';
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useState,
} from 'react';

import classNames from 'classnames';
import { useCombobox } from 'downshift';

import styles from './SearchableSelect.module.css';

import { useElementState } from '../../hooks/useElementState';
import { Caret } from '../../icons/Caret';
import { Chevron } from '../../icons/Chevron';
import { FieldLabel } from '../FieldLabel';
import type { StyledSelectIcon } from '../StyledSelect';
import type { StyledOptionProps } from '../StyledSelect/components';
import {
  MultiButton,
  MultiButtonList,
  StyledOption,
  StyledOptionList,
} from '../StyledSelect/components';
import { getHasValue } from '../StyledSelect/getHasValue';
import type { MultiProps } from '../StyledSelect/hooks';
import { useMultiSelect } from '../StyledSelect/hooks';
import { getValue, stringify } from '../StyledSelect/utils/option';
import { byIgnoreFilter, byInputValue } from '../StyledSelect/utils/search';
import { Text } from '../Text';

export interface SearchableSelectProps extends MultiProps {
  /** Label and placeholder of select */
  label: string;
  /** Display invalid state */
  invalid?: boolean;
  /** The value of the combobox when used in a controlled state */
  value?: string | number | string[] | number[];
  /** String value to ignore when searching */
  ignoreFilter?: string;
  onChange?: (event?: { value?: string | number; checked: boolean }) => void;
  children: ReactElement<StyledOptionProps>[];
  className?: string;
  /** Display highlighted border state */
  highlighted?: boolean;
  /** Limits the amount of values that can be selected for multi select */
  max?: number;
  /** Hide FieldLabel but keep it in the accessibility tree */
  hideLabel?: boolean;
  /** Dropdown icon style */
  icon?: StyledSelectIcon;
  /** Apply bold font weight to selected options in list */
  bold?: boolean;
}

/**
 * Render a searchable {@link StyledSelect} form element. Implements
 * {@link https://downshift-js.com}.
 *
 * @cssVariable --tfd-searchableselect-height The height of the SearchableSelect's button and input
 * @see https://corgi-x.tfd.engineering/components/searchableselect
 */
export function SearchableSelect(props: SearchableSelectProps) {
  const {
    label,
    children,
    ignoreFilter,
    withFreeForm,
    className,
    highlighted,
    invalid,
    max,
    hideLabel = false,
    icon = 'caret',
    bold = false,
    'aria-description': ariaDescription,
    ...restProps
  } = props;

  const [state, handlers] = useElementState({ highlighted, invalid });
  const options = Children.toArray(children) as typeof children;

  const { value, hasValue, getIsSelected, handleChange, removeValue } =
    useMultiSelect(restProps);

  const isMultiMaxed =
    restProps.multi && Array.isArray(value) && value.length === max;

  const [filteredOptions, setFilteredOptions] = useState(options);

  useEffect(() => {
    setFilteredOptions(Children.toArray(children) as typeof children);
  }, [children]);

  const {
    isOpen,
    highlightedIndex,
    openMenu,
    inputValue,
    setInputValue,
    ...combobox
  } = useCombobox({
    items: filteredOptions,
    itemToString: stringify,
    selectedItem: null,
    isItemDisabled: item => item.props.disabled ?? false,
    onSelectedItemChange: changes => {
      handleChange(changes);
      if (restProps.multi) {
        setInputValue('');
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-shadow
    onInputValueChange: ({ inputValue }) => {
      const nextInputValue = inputValue || '';

      const nextFilteredOptions = options
        .filter(byInputValue(nextInputValue, ignoreFilter))
        .sort(byIgnoreFilter(ignoreFilter));

      if (withFreeForm && nextInputValue) {
        nextFilteredOptions.push(
          <StyledOption
            key="freeForm"
            value={inputValue}
          >{`“${nextInputValue}”`}</StyledOption>
        );
      }

      setFilteredOptions(nextFilteredOptions);
    },
    // eslint-disable-next-line @typescript-eslint/no-shadow
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      if (isMultiMaxed) {
        return {
          ...state,
          isOpen: false,
        };
      }
      switch (type) {
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: false,
            highlightedIndex: state.highlightedIndex,
            inputValue: '',
          };
        case useCombobox.stateChangeTypes.InputKeyDownEnter: {
          // Only allow selection when menu is open
          if (!state.isOpen) {
            return changes;
          }

          // Preserve downshift's selection if it exists (highlighted item)
          let selectedItem = changes.selectedItem;

          // If no selection and conditions met, select free-form option
          if (
            !selectedItem &&
            state.inputValue &&
            (state.highlightedIndex === null || state.highlightedIndex === -1)
          ) {
            // Compute filtered options from state.inputValue to avoid stale closure
            // This matches the logic in onInputValueChange
            const nextInputValue = state.inputValue || '';
            const computedFilteredOptions = options
              .filter(byInputValue(nextInputValue, ignoreFilter))
              .sort(byIgnoreFilter(ignoreFilter));

            // First, check if any real option's display text exactly matches the input
            // This prioritizes real options over free-form when input matches exactly
            const exactMatchOption = computedFilteredOptions.find(option => {
              if (!isValidElement(option)) {
                return false;
              }
              const optionDisplayText = stringify(option);
              return (
                optionDisplayText.toLowerCase() === nextInputValue.toLowerCase()
              );
            });

            if (exactMatchOption && isValidElement(exactMatchOption)) {
              selectedItem = exactMatchOption;
            } else if (withFreeForm && nextInputValue) {
              // Only select free-form if no real option matches exactly
              const freeFormOption = (
                <StyledOption
                  key="freeForm"
                  value={state.inputValue}
                >{`“${state.inputValue}”`}</StyledOption>
              );
              selectedItem = freeFormOption;
            }
          }

          return {
            ...changes,
            selectedItem: selectedItem ?? changes.selectedItem,
            isOpen: false,
            highlightedIndex: state.highlightedIndex,
            inputValue: '',
          };
        }
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: '',
            /* if the user hits the tab key while there are any items
            selected, this option ensures the item doesn't get deselected */
            selectedItem: null,
          };
        default:
          return changes;
      }
    },
  });

  const toggleButtonProps = combobox.getToggleButtonProps(handlers);

  return (
    <FieldLabel
      as="div"
      label={label}
      state={state}
      in={Boolean(
        state === 'focus' ||
        hasValue ||
        getHasValue(inputValue) ||
        state === 'invalid'
      )}
      hideLabel={hideLabel}
      className={classNames(
        styles.label,
        Array.isArray(value) && value.length > 0 ? styles['has-value'] : '',
        className
      )}
      {
        /* getLabelProps returns an htmlFor attribute which isn't allowed on "div" elements */
        ...(combobox.getLabelProps() as JSX.IntrinsicElements['div'])
      }
    >
      <StyledOptionList
        {...combobox.getMenuProps(handlers)}
        isOpen={isOpen}
        positionElement={toggleButtonProps.ref?.current}
        positionKey={Array.isArray(value) ? String(value.length) : '0'}
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map((child, index) => {
            if (!isValidElement(child)) {
              return child;
            }

            return cloneElement(child, {
              hover: index === highlightedIndex,
              selected: getIsSelected(child),
              bold: getIsSelected(child) && bold,
              match: inputValue,
              ...combobox.getItemProps({
                index,
                item: child,
              }),
            });
          })
        ) : (
          <StyledOption bold invalid>
            No options matched
          </StyledOption>
        )}
      </StyledOptionList>
      <div
        className={classNames(styles.container, {
          [styles.isMultiMaxed]: isMultiMaxed,
        })}
      >
        {restProps.multi && Array.isArray(value) && value.length > 0 && (
          <MultiButtonList>
            {value.map((selectedValue: string | number) => {
              let optionComponent = options.find(
                option => getValue(option) === selectedValue
              );

              if (!optionComponent && withFreeForm) {
                optionComponent = <span>{selectedValue}</span>;
              }

              return (
                <MultiButton
                  key={selectedValue}
                  onClick={(): void => removeValue(selectedValue)}
                >
                  {optionComponent?.props.label ?? stringify(optionComponent)}
                </MultiButton>
              );
            })}
          </MultiButtonList>
        )}
        <Text asChild color="Kale3">
          <input
            aria-description={ariaDescription}
            className={classNames(styles.input, {
              [styles.isMultiMaxed]: isMultiMaxed,
            })}
            {...combobox.getInputProps({
              ...handlers,
              onFocus: e => {
                handlers.onFocus(e);
                if (!isOpen) {
                  toggleButtonProps.onFocus(e);
                }
              },
            })}
          />
        </Text>
        <Text asChild color="Kale3">
          <button
            {...toggleButtonProps}
            className={classNames(styles.button, {
              [styles.isMultiMaxed]: isMultiMaxed,
            })}
            aria-label="Toggle Listbox"
            type="button"
          >
            {icon === 'chevron' ? (
              <Chevron
                aria-hidden
                size={36}
                baseColor="Kale3"
                className={classNames(styles.buttonIcon, styles[icon], {
                  [styles.isOpen]: isOpen,
                })}
              />
            ) : (
              <Caret
                aria-hidden
                size={36}
                baseColor="Kale3"
                className={classNames(styles.buttonIcon, {
                  [styles.isOpen]: isOpen,
                })}
              />
            )}
          </button>
        </Text>
      </div>
    </FieldLabel>
  );
}
